// Frontend-side action dispatcher. Routes the (subset of) actions we
// build via bridge-sdk-js — log_metadata + is_transfer_finalised — to
// the right per-chain builder, then signs with the connected wallet.
//
// Only the chain-side cases live here; everything NEAR-side goes via
// /api/actions/{slug}/build on the backend (see RecoveryPage::submit).

import { createEvmBuilder } from "@omni-bridge/evm";
import { createSolanaBuilder, createFogoBuilder } from "@omni-bridge/solana";
import { createStarknetBuilder } from "@omni-bridge/starknet";
import { ChainKind } from "@omni-bridge/core";
import type { WalletClient } from "viem";
import { Connection, PublicKey, Transaction, TransactionMessage, VersionedTransaction } from "@solana/web3.js";
import type { WalletContextState } from "@solana/wallet-adapter-react";

import type { ActionDescriptor, UnsignedNearTx } from "@/types/dto";
import type { ActionOutcome } from "@/components/ResultCard";
import type { Network } from "@/config/network";
import { chains } from "@/config/chains";
import { evmExplorerTx, solExplorerTx, fogoExplorerTx, starknetExplorerTx, nearExplorerTx } from "@/lib/explorer";
import {
  buildNearFinTransfer,
  FinTransferUnsupported,
  isFinOriginChain,
} from "@/wallet/finTransfer";
import { buildDeployToken, buildBindToken } from "@/wallet/tokenDeploy";
import { buildFastFinTransfer } from "@/wallet/fastFin";
import { buildSubmitBtcTransfer } from "@/wallet/submitBtc";
import { signTransferPayload } from "@/api/client";
import {
  evmFinalizationPayload,
  solanaFinalizationPayload,
  starknetFinalizationPayload,
  mpcSignatureBytes,
  isNativeSvmToken,
  buildNativeSvmFinalization,
} from "@/wallet/finalizeDestination";

export interface FrontendDispatchArgs {
  descriptor: ActionDescriptor;
  body: Record<string, unknown>;
  network: Network;
  wallets: {
    evm: {
      walletClient: WalletClient | undefined;
      sendTransaction: NonNullable<
        ReturnType<typeof import("wagmi").useSendTransaction>["sendTransactionAsync"]
      >;
      /** EIP-155 chain id the wallet is currently on (undefined when no
       *  EVM wallet is connected). Used to decide whether we need to
       *  prompt the wallet to switch before signing. */
      currentChainId: number | undefined;
      /** wagmi's `switchChainAsync` — opens the wallet's network-switch
       *  prompt for the requested EIP-155 chain. wagmi's
       *  `sendTransaction({ chainId })` throws a ChainMismatchError if
       *  the wallet isn't already there (it won't auto-switch), so the
       *  dispatcher calls this first when needed. */
      switchChain: NonNullable<
        ReturnType<typeof import("wagmi").useSwitchChain>["switchChainAsync"]
      >;
    };
    sol: {
      wallet: WalletContextState;
      connection: Connection;
    };
    starknet: {
      sendAsync: NonNullable<
        ReturnType<typeof import("@starknet-react/core").useSendTransaction>["sendAsync"]
      >;
    };
    near: {
      account: string | null;
      signAndSend: (tx: UnsignedNearTx) => Promise<string>;
    };
  };
}

export async function dispatchFrontend(args: FrontendDispatchArgs): Promise<ActionOutcome> {
  const { descriptor } = args;
  switch (descriptor.kind) {
    case "log-metadata":
      return logMetadata(args);
    case "is-transfer-finalised":
      return isTransferFinalised(args);
    case "near-fin-transfer":
      return nearFinTransfer(args);
    case "deploy-token":
      return deployOrBindToken(args, "deploy");
    case "bind-token":
      return deployOrBindToken(args, "bind");
    case "near-fast-fin-transfer":
      return nearFastFinTransfer(args);
    case "near-submit-btc-transfer":
      return submitBtcTransfer(args);
    case "evm-fin-transfer":
      return evmFinTransfer(args);
    case "sol-fin-transfer":
      return svmFinTransfer(args);
    case "starknet-fin-transfer":
      return starknetFinTransfer(args);
    default:
      return {
        kind: "errored",
        message: `dispatchFrontend has no route for ${descriptor.kind}`,
      };
  }
}

// ─── deploy_token / bind_token ───────────────────────────────────────────────

async function deployOrBindToken(
  args: FrontendDispatchArgs,
  kind: "deploy" | "bind",
): Promise<ActionOutcome> {
  const signerId = args.wallets.near.account;
  if (!signerId) {
    return { kind: "errored", message: `Connect a NEAR wallet first — ${kind}_token signs a NEAR transaction.` };
  }
  // deploy-token names the source as `source_chain`; bind-token as `chain`.
  const sourceChain =
    kind === "deploy" ? String(args.body.source_chain ?? "") : String(args.body.chain ?? "");
  const txHash = String(args.body.tx_hash ?? "");
  if (!sourceChain || !txHash) {
    return {
      kind: "errored",
      message: kind === "deploy" ? "source_chain and tx_hash are required." : "chain and tx_hash are required.",
    };
  }
  try {
    const { tx } =
      kind === "deploy"
        ? await buildDeployToken({ network: args.network, sourceChain, txHash, signerId })
        : await buildBindToken({ network: args.network, sourceChain, txHash, signerId });
    const hash = await args.wallets.near.signAndSend(tx);
    return { kind: "tx", hash, explorerUrl: nearExplorerTx(args.network, hash) };
  } catch (e) {
    if (e instanceof FinTransferUnsupported) {
      return { kind: "needs_bridge_cli", message: e.message };
    }
    throw e;
  }
}

// ─── near_submit_btc_transfer (unstick a stuck UTXO withdrawal) ──────────────

async function submitBtcTransfer(args: FrontendDispatchArgs): Promise<ActionOutcome> {
  const signerId = args.wallets.near.account;
  if (!signerId) {
    return { kind: "errored", message: "Connect a NEAR wallet first — this signs a NEAR transaction." };
  }
  const chain = String(args.body.chain ?? "");
  if (chain !== "Btc" && chain !== "Zcash") {
    return { kind: "errored", message: `chain must be Btc or Zcash, got "${chain}".` };
  }
  const nearTxHash = String(args.body.near_tx_hash ?? "");
  if (!nearTxHash) {
    return { kind: "errored", message: "near_tx_hash is required." };
  }
  // sender_id defaults to the connected NEAR account (the usual case: the
  // operator is unsticking their own withdrawal).
  const senderId = String(args.body.sender_id ?? "") || signerId;
  const feeRateStr = String(args.body.fee_rate ?? "");
  let feeRate: number | null = null;
  if (feeRateStr) {
    const n = Number(feeRateStr);
    if (!Number.isFinite(n) || n <= 0) {
      return { kind: "errored", message: `fee_rate must be a positive number, got "${feeRateStr}".` };
    }
    feeRate = n;
  }
  const { tx } = await buildSubmitBtcTransfer({
    network: args.network,
    chain,
    nearTxHash,
    senderId,
    feeRate,
    signerId,
  });
  const hash = await args.wallets.near.signAndSend(tx);
  return { kind: "tx", hash, explorerUrl: nearExplorerTx(args.network, hash) };
}

// ─── near_fast_fin_transfer (relayer-economic) ───────────────────────────────

async function nearFastFinTransfer(args: FrontendDispatchArgs): Promise<ActionOutcome> {
  const relayer = args.wallets.near.account;
  if (!relayer) {
    return {
      kind: "errored",
      message:
        "Connect a NEAR wallet first — fast-fin fronts liquidity from the connected " +
        "(relayer) account, which signs the ft_transfer_call.",
    };
  }
  const originChain = String(args.body.origin_chain ?? "");
  const txHash = String(args.body.tx_hash ?? "");
  if (!originChain || !txHash) {
    return { kind: "errored", message: "origin_chain and tx_hash are required." };
  }
  let storageDepositAmount: bigint | null = null;
  const sda = String(args.body.storage_deposit_amount ?? "");
  if (sda) {
    try {
      storageDepositAmount = BigInt(sda);
    } catch {
      return { kind: "errored", message: `storage_deposit_amount must be an integer, got "${sda}".` };
    }
  }
  try {
    const { tx } = await buildFastFinTransfer({
      network: args.network,
      originChain,
      txHash,
      storageDepositAmount,
      relayer,
    });
    const hash = await args.wallets.near.signAndSend(tx);
    return { kind: "tx", hash, explorerUrl: nearExplorerTx(args.network, hash) };
  } catch (e) {
    if (e instanceof FinTransferUnsupported) {
      return { kind: "needs_bridge_cli", message: e.message };
    }
    throw e;
  }
}

// ─── near_fin_transfer ──────────────────────────────────────────────────────

async function nearFinTransfer(args: FrontendDispatchArgs): Promise<ActionOutcome> {
  const signerId = args.wallets.near.account;
  if (!signerId) {
    return { kind: "errored", message: "Connect a NEAR wallet first — fin_transfer signs a NEAR transaction." };
  }
  const dest = String(args.body.destination_chain ?? "");
  if (dest !== "Near") {
    return {
      kind: "errored",
      message: `This action finalizes transfers whose destination is NEAR; got destination_chain=${dest || "(none)"}.`,
    };
  }
  const originRaw = String(args.body.origin_chain ?? "");
  const txHash = String(args.body.tx_hash ?? "");
  if (!originRaw || !txHash) {
    return { kind: "errored", message: "origin_chain and tx_hash are required." };
  }
  // Validate the origin against the supported set rather than blindly
  // casting — an unknown chain (e.g. Ton/Sui) is a real red error, not the
  // amber "use bridge-cli" MPC fallback that `buildNearFinTransfer` reserves
  // for Abs/Strk.
  if (!isFinOriginChain(originRaw)) {
    return {
      kind: "errored",
      message: `Unsupported origin chain for fin_transfer: ${originRaw}. Supported: Eth, Arb, Base, Bnb, Polygon, Sol, Abs, Starknet.`,
    };
  }
  try {
    const { tx } = await buildNearFinTransfer({
      network: args.network,
      originChain: originRaw,
      txHash,
      signerId,
    });
    const hash = await args.wallets.near.signAndSend(tx);
    return { kind: "tx", hash, explorerUrl: nearExplorerTx(args.network, hash) };
  } catch (e) {
    if (e instanceof FinTransferUnsupported) {
      // Genuinely-hard origins (MPC proofs) + not-yet-wired paths surface
      // as an amber "use bridge-cli" outcome rather than a red error.
      return { kind: "needs_bridge_cli", message: e.message };
    }
    throw e;
  }
}

// ─── log_metadata ───────────────────────────────────────────────────────

async function logMetadata(args: FrontendDispatchArgs): Promise<ActionOutcome> {
  const tokenInput = String(args.body.token ?? "");
  const [prefix, addr] = splitOmniAddress(tokenInput);
  if (!prefix || !addr) {
    return { kind: "errored", message: `Token must be in <chain>:<address> form, got \`${tokenInput}\`` };
  }
  if (prefix === "eth" || prefix === "arb" || prefix === "base" || prefix === "bnb" || prefix === "pol" || prefix === "abs") {
    return logMetadataEvm(args, prefix, addr);
  }
  if (prefix === "sol" || prefix === "solana") {
    return logMetadataSvm(args, addr, false);
  }
  if (prefix === "fogo") {
    return logMetadataSvm(args, addr, true);
  }
  if (prefix === "strk" || prefix === "starknet") {
    return logMetadataStarknet(args, addr);
  }
  return { kind: "errored", message: `log_metadata: unsupported chain prefix \`${prefix}\`` };
}

// ─── destination-side fin_transfer (EVM / Solana / Starknet) ─────────────────
// A NEAR-originated transfer is finalized on the destination chain by feeding
// the NEAR `sign_transfer` MPC signature + payload (scraped from the NEAR tx by
// the backend) into the chain's SDK `buildFinalization`, then signing with that
// chain's wallet. The pure payload mapping lives in finalizeDestination.ts.

/** NEAR account that signed the `sign_transfer` tx — needed for the backend
 *  `tx_status` scrape. An explicit `sender_id` field wins; else the connected
 *  NEAR account (the common case where the operator signed it). */
function finSenderId(args: FrontendDispatchArgs): string {
  return String(args.body.sender_id ?? "") || args.wallets.near.account || "";
}

const NO_FIN_SENDER_MSG =
  "Connect the NEAR account that signed `sign_transfer` (or provide a sender_id) so the backend can read its receipt.";

async function evmFinTransfer(args: FrontendDispatchArgs): Promise<ActionOutcome> {
  const dest = String(args.body.destination_chain ?? "");
  const key = EVM_CHAIN_KEYS[dest];
  if (!key) {
    return { kind: "errored", message: `evm-fin-transfer: unsupported destination chain ${dest || "(none)"}.` };
  }
  const nearTxHash = String(args.body.near_tx_hash ?? "");
  if (!nearTxHash) return { kind: "errored", message: "near_tx_hash is required." };
  const senderId = finSenderId(args);
  if (!senderId) return { kind: "errored", message: NO_FIN_SENDER_MSG };

  const cfg = chains(args.network).evm[key];
  const event = await signTransferPayload(nearTxHash, senderId);
  const builder = createEvmBuilder({
    network: args.network,
    chain: PREFIX_TO_CHAIN_KIND_EVM[key] as Parameters<typeof createEvmBuilder>[0]["chain"],
  });
  const tx = builder.buildFinalization(
    evmFinalizationPayload(event.message_payload),
    mpcSignatureBytes(event.signature, true),
  );
  await ensureEvmChain(args, cfg.id);
  const hash = await args.wallets.evm.sendTransaction({
    chainId: cfg.id,
    to: tx.to,
    data: tx.data,
    value: tx.value,
  });
  return { kind: "tx", hash, explorerUrl: evmExplorerTx(args.network, cfg.id, hash) };
}

// Handles both Solana and Fogo (SVM) — same omni-bridge program family, same
// Solana-family wallet. The destination chain selects the builder + the
// connection: Solana uses the wallet's connection; Fogo (a separate SVM
// network) gets a connection to the Fogo RPC, and the same wallet signs +
// submits there.
async function svmFinTransfer(args: FrontendDispatchArgs): Promise<ActionOutcome> {
  const { network } = args;
  const { wallet } = args.wallets.sol;
  if (!wallet.publicKey) {
    return { kind: "errored", message: "Connect a Solana-family wallet to finalize on Solana / Fogo." };
  }
  const dest = String(args.body.destination_chain ?? "");
  const isFogo = dest === "Fogo";
  if (dest !== "Sol" && !isFogo) {
    return { kind: "errored", message: `sol-fin-transfer: unsupported SVM destination ${dest || "(none)"}.` };
  }
  const nearTxHash = String(args.body.near_tx_hash ?? "");
  if (!nearTxHash) return { kind: "errored", message: "near_tx_hash is required." };
  const senderId = finSenderId(args);
  if (!senderId) return { kind: "errored", message: NO_FIN_SENDER_MSG };

  const connection = isFogo
    ? new Connection(chains(network).fogo.rpcUrl, "confirmed")
    : args.wallets.sol.connection;
  const event = await signTransferPayload(nearTxHash, senderId);
  const builder = isFogo
    ? createFogoBuilder({ network, connection })
    : createSolanaBuilder({ network, connection });
  const sigBytes = mpcSignatureBytes(event.signature, false);
  // Native SOL (token = the System Program marker) has its own on-chain
  // instruction (`finalize_transfer_sol`) that the SDK's SPL-only
  // `buildFinalization` can't produce — hand-build it. SPL mints go through
  // the SDK as normal.
  const instructions = isNativeSvmToken(event.message_payload.token_address)
    ? await buildNativeSvmFinalization(builder, event.message_payload, sigBytes, wallet.publicKey)
    : await builder.buildFinalization(
        solanaFinalizationPayload(event.message_payload),
        { toBytes: () => sigBytes }, // SDK's SolanaMPCSignature is `{ toBytes(): Uint8Array }`
        wallet.publicKey,
      );
  // The payer (the connected wallet) covers the tx fee + Wormhole post-message
  // fee + message rent — the finalized SOL itself comes from the bridge vault.
  // An unfunded payer is the usual cause of the wallet's opaque "Internal
  // error", so check up front and say so plainly.
  if ((await connection.getBalance(wallet.publicKey)) === 0) {
    return {
      kind: "errored",
      message:
        `Your Solana wallet (${wallet.publicKey.toBase58()}) has 0 SOL on this cluster, so it ` +
        `can't pay the transaction + Wormhole fees.` +
        (network === "testnet"
          ? " Fund it on devnet — e.g. `solana airdrop 2 " +
            wallet.publicKey.toBase58() +
            " --url https://api.devnet.solana.com` — and retry."
          : " Fund it and retry."),
    };
  }

  const { blockhash } = await connection.getLatestBlockhash("confirmed");

  // Pre-flight against our own RPC (sigVerify off) so we surface the REAL
  // on-chain reason — wallets collapse any failure into a blanket "Internal
  // error". Best-effort: if simulate is unavailable, fall through to the wallet.
  try {
    const simMsg = new TransactionMessage({
      payerKey: wallet.publicKey,
      recentBlockhash: blockhash,
      instructions,
    }).compileToV0Message();
    const sim = await connection.simulateTransaction(new VersionedTransaction(simMsg), {
      sigVerify: false,
      replaceRecentBlockhash: true,
    });
    if (sim.value.err) {
      const tail = (sim.value.logs ?? []).slice(-8).join("\n");
      return {
        kind: "errored",
        message: `Solana pre-flight failed: ${JSON.stringify(sim.value.err)}${tail ? `\n${tail}` : ""}`,
      };
    }
  } catch {
    // simulateTransaction unavailable on this RPC — let the wallet attempt it.
  }

  const tx = new Transaction({ feePayer: wallet.publicKey, recentBlockhash: blockhash });
  tx.add(...instructions);
  // We validated the tx ourselves above; skip the wallet's own preflight, which
  // can spuriously fail with "Internal error" on the Wormhole-CPI finalize tx.
  const hash = await wallet.sendTransaction(tx, connection, { skipPreflight: true });
  return {
    kind: "tx",
    hash,
    explorerUrl: isFogo ? fogoExplorerTx(network, hash) : solExplorerTx(network, hash),
  };
}

async function starknetFinTransfer(args: FrontendDispatchArgs): Promise<ActionOutcome> {
  const { network } = args;
  const nearTxHash = String(args.body.near_tx_hash ?? "");
  if (!nearTxHash) return { kind: "errored", message: "near_tx_hash is required." };
  const senderId = finSenderId(args);
  if (!senderId) return { kind: "errored", message: NO_FIN_SENDER_MSG };

  const event = await signTransferPayload(nearTxHash, senderId);
  const builder = createStarknetBuilder({ network });
  const calls = builder.buildFinalization(
    mpcSignatureBytes(event.signature, false),
    starknetFinalizationPayload(event.message_payload),
  );
  const { sendAsync } = args.wallets.starknet;
  const result = await sendAsync(calls);
  const hash = (result as { transaction_hash?: string } | undefined)?.transaction_hash;
  if (!hash) {
    throw new Error(
      "Starknet wallet returned no transaction_hash — sign was rejected or the wallet replied with an unexpected shape.",
    );
  }
  return { kind: "tx", hash, explorerUrl: starknetExplorerTx(network, hash) };
}

async function logMetadataEvm(
  args: FrontendDispatchArgs,
  prefix: "eth" | "arb" | "base" | "bnb" | "pol" | "abs",
  token: string,
): Promise<ActionOutcome> {
  const { network } = args;
  const cfg = chains(network).evm[prefix];
  // bridge-sdk-js's EVM builder takes a narrower `EvmChainKind` that's
  // not exported, so we cast the lookup back to the union it accepts.
  const builder = createEvmBuilder({
    network,
    chain: PREFIX_TO_CHAIN_KIND_EVM[prefix] as Parameters<typeof createEvmBuilder>[0]["chain"],
  });
  const tx = builder.buildLogMetadata(token as `0x${string}`);
  await ensureEvmChain(args, cfg.id);
  const { sendTransaction } = args.wallets.evm;
  const hash = await sendTransaction({
    chainId: cfg.id,
    to: tx.to,
    data: tx.data,
    value: tx.value,
  });
  return {
    kind: "tx",
    hash,
    explorerUrl: evmExplorerTx(network, cfg.id, hash),
  };
}

/** Prompt the connected EVM wallet to switch to `targetChainId` when it
 *  isn't already there. wagmi's `sendTransaction({ chainId })` throws a
 *  ChainMismatchError ("The current chain of the wallet (id: X) does not
 *  match the target chain (id: Y)") rather than auto-switching, so we
 *  bridge that gap here. No-ops when the wallet is already on the right
 *  chain or when the chain id is unknown (no EVM wallet connected).
 */
async function ensureEvmChain(args: FrontendDispatchArgs, targetChainId: number): Promise<void> {
  const current = args.wallets.evm.currentChainId;
  if (current === targetChainId) return;
  if (current === undefined) {
    // Defensive — chainForAction's "evm" gate should have caught a
    // missing wallet earlier. Surface the same message wagmi would.
    throw new Error("Connect an EVM wallet first — no chain id available.");
  }
  await args.wallets.evm.switchChain({ chainId: targetChainId });
}

// Handles Solana + Fogo (SVM, same omni-bridge program family + Solana-family
// wallet). Fogo is a separate SVM network: it gets a connection to the Fogo RPC
// and the same wallet signs + submits there. Mirrors `svmFinTransfer`.
async function logMetadataSvm(
  args: FrontendDispatchArgs,
  mint: string,
  isFogo: boolean,
): Promise<ActionOutcome> {
  const { network } = args;
  const { wallet } = args.wallets.sol;
  if (!wallet.publicKey) {
    return {
      kind: "errored",
      message: `Connect a Solana-family wallet to log metadata on ${isFogo ? "Fogo" : "Solana"}.`,
    };
  }
  const connection = isFogo
    ? new Connection(chains(network).fogo.rpcUrl, "confirmed")
    : args.wallets.sol.connection;
  const builder = isFogo
    ? createFogoBuilder({ network, connection })
    : createSolanaBuilder({ network, connection });
  const chainLabel = isFogo ? "Fogo" : "Solana";
  let mintKey: PublicKey;
  try {
    mintKey = new PublicKey(mint);
  } catch {
    return { kind: "errored", message: `\`${mint}\` is not a valid ${chainLabel} mint address (base58).` };
  }
  // `buildLogMetadata` reads the mint account on the destination chain's RPC.
  // The usual failure is a wrong-chain mint (e.g. a Solana SPL mint pasted as a
  // \`fogo:\` token) — surface that plainly instead of the SDK's opaque
  // "Failed to find mint account", which a wallet wrapper labels "Wallet: …".
  let instructions;
  try {
    instructions = await builder.buildLogMetadata(mintKey, wallet.publicKey);
  } catch (e) {
    const msg = String((e as { message?: unknown })?.message ?? e);
    if (/mint account|not a (valid )?mint|find mint|TokenInvalidAccountOwner|TokenAccountNotFound/i.test(msg)) {
      return {
        kind: "errored",
        message: `\`${mint}\` is not an SPL token mint on ${chainLabel} (${network}). Provide a mint that exists on ${chainLabel}.`,
      };
    }
    throw e;
  }
  // The payer (connected wallet) covers the tx fee + Wormhole post-message fee
  // + metadata account rent. An unfunded payer is the usual cause of the
  // wallet's opaque "Internal error" — check up front and say so plainly.
  if ((await connection.getBalance(wallet.publicKey)) === 0) {
    const fund =
      !isFogo && network === "testnet"
        ? ` Fund it on devnet — \`solana airdrop 2 ${wallet.publicKey.toBase58()} --url https://api.devnet.solana.com\` — and retry.`
        : " Fund it and retry.";
    return {
      kind: "errored",
      message:
        `Your wallet (${wallet.publicKey.toBase58()}) has 0 balance on ${chainLabel} (${network}), ` +
        `so it can't pay the transaction + Wormhole fees.${fund}`,
    };
  }

  const { blockhash } = await connection.getLatestBlockhash("confirmed");
  // Pre-flight against our own RPC (sigVerify off) so we surface the REAL
  // on-chain reason — wallets collapse any failure into a blanket "Internal
  // error". Best-effort: if simulate is unavailable, fall through to the wallet.
  try {
    const simMsg = new TransactionMessage({
      payerKey: wallet.publicKey,
      recentBlockhash: blockhash,
      instructions,
    }).compileToV0Message();
    const sim = await connection.simulateTransaction(new VersionedTransaction(simMsg), {
      sigVerify: false,
      replaceRecentBlockhash: true,
    });
    if (sim.value.err) {
      const tail = (sim.value.logs ?? []).slice(-8).join("\n");
      return {
        kind: "errored",
        message: `${chainLabel} pre-flight failed: ${JSON.stringify(sim.value.err)}${tail ? `\n${tail}` : ""}`,
      };
    }
  } catch {
    // simulateTransaction unavailable on this RPC — let the wallet attempt it.
  }

  const tx = new Transaction({ feePayer: wallet.publicKey, recentBlockhash: blockhash });
  tx.add(...instructions);
  // We validated the tx ourselves above; skip the wallet's own preflight, which
  // can spuriously fail with "Internal error" on the Wormhole-CPI tx.
  const signature = await wallet.sendTransaction(tx, connection, { skipPreflight: true });
  return {
    kind: "tx",
    hash: signature,
    explorerUrl: isFogo ? fogoExplorerTx(network, signature) : solExplorerTx(network, signature),
  };
}

async function logMetadataStarknet(
  args: FrontendDispatchArgs,
  token: string,
): Promise<ActionOutcome> {
  const { network } = args;
  const builder = createStarknetBuilder({ network });
  const calls = builder.buildLogMetadata(token);
  const { sendAsync } = args.wallets.starknet;
  const result = await sendAsync(calls);
  const hash = (result as { transaction_hash?: string } | undefined)?.transaction_hash;
  if (!hash) {
    // Surface the empty-hash case as a real error instead of rendering
    // a green "success" with an empty explorer link.
    throw new Error(
      "Starknet wallet returned no transaction_hash — sign was rejected or the wallet replied with an unexpected shape.",
    );
  }
  return {
    kind: "tx",
    hash,
    explorerUrl: starknetExplorerTx(network, hash),
  };
}

// ─── is_transfer_finalised ──────────────────────────────────────────────

async function isTransferFinalised(args: FrontendDispatchArgs): Promise<ActionOutcome> {
  // bridge-sdk-js doesn't expose this as a single helper — the EVM,
  // Sol, and Starknet flavours each read different storage. We
  // implement them with viem / @solana/web3.js / starknet directly,
  // mirroring what the (deleted) backend code used to do.
  const dest = String(args.body.destination_chain ?? "");
  const nonceStr = String(args.body.nonce ?? "0");
  const nonce = BigInt(nonceStr);
  if (["Eth", "Arb", "Base", "Bnb", "Polygon", "Abs"].includes(dest)) {
    return evmIsFinalised(args, dest, nonce);
  }
  if (dest === "Sol") {
    return solIsFinalised(args, nonce);
  }
  if (dest === "Starknet") {
    return starknetIsFinalised(args, nonce);
  }
  return { kind: "errored", message: `is_transfer_finalised: unsupported chain ${dest}` };
}

const EVM_CHAIN_KEYS: Record<string, keyof ReturnType<typeof chains>["evm"]> = {
  Eth: "eth",
  Arb: "arb",
  Base: "base",
  Bnb: "bnb",
  Polygon: "pol",
  Abs: "abs",
};

async function evmIsFinalised(
  args: FrontendDispatchArgs,
  dest: string,
  nonce: bigint,
): Promise<ActionOutcome> {
  const cfg = chains(args.network).evm[EVM_CHAIN_KEYS[dest] ?? "eth"];
  const { createPublicClient, http, encodeFunctionData } = await import("viem");
  const client = createPublicClient({ transport: http(cfg.rpcUrl) });
  const data = encodeFunctionData({
    abi: [
      {
        type: "function",
        name: "completedTransfers",
        inputs: [{ name: "nonce", type: "uint64" }],
        outputs: [{ type: "bool" }],
        stateMutability: "view",
      },
    ] as const,
    functionName: "completedTransfers",
    args: [nonce],
  });
  const result = await client.call({ to: cfg.omniBridgeAddress as `0x${string}`, data });
  // viem returns `0x` (empty bytes) when the contract doesn't exist at
  // this address OR when the call reverted with no data. `BigInt("0x")`
  // throws a SyntaxError, which would bubble as a confusing parse
  // failure — treat empty as "not finalised" (matches what the contract
  // would say if the call did succeed and the slot was unset).
  const hex = result.data ?? "0x";
  const finalised = hex !== "0x" && hex !== "0x0" && BigInt(hex) !== 0n;
  return { kind: "view", value: finalised };
}

async function solIsFinalised(args: FrontendDispatchArgs, nonce: bigint): Promise<ActionOutcome> {
  // PDA: `[b"used_nonces", (nonce / 1024).to_le_bytes()]` under the
  // omni-bridge program. The bucket's first 8 bytes are the Anchor
  // discriminator; the next 128 bytes are a packed bitmap. Bit
  // `nonce % 1024` tells us whether the transfer with that nonce is
  // finalised.
  const { network, wallets } = args;
  const { connection } = wallets.sol;
  const cfg = chains(network).solana;
  const programId = new PublicKey(cfg.programId);
  const bucket = nonce / 1024n;
  const bucketBytes = new Uint8Array(8);
  let v = bucket;
  for (let i = 0; i < 8; i++) {
    bucketBytes[i] = Number(v & 0xffn);
    v >>= 8n;
  }
  const [pda] = PublicKey.findProgramAddressSync(
    [new TextEncoder().encode("used_nonces"), bucketBytes],
    programId,
  );
  const account = await connection.getAccountInfo(pda);
  if (!account) {
    return { kind: "view", value: false };
  }
  const data = account.data;
  if (data.length < 8 + 128) {
    return {
      kind: "errored",
      message: `used_nonces account too small (${data.length} bytes; need 136)`,
    };
  }
  const bitIndex = Number(nonce % 1024n);
  const byte = data[8 + Math.floor(bitIndex / 8)] ?? 0;
  const finalised = ((byte >> (bitIndex % 8)) & 1) === 1;
  return { kind: "view", value: finalised };
}

async function starknetIsFinalised(args: FrontendDispatchArgs, nonce: bigint): Promise<ActionOutcome> {
  const cfg = chains(args.network).starknet;
  const { RpcProvider } = await import("starknet");
  const provider = new RpcProvider({ nodeUrl: cfg.rpcUrl });
  const result = await provider.callContract({
    contractAddress: cfg.omniBridgeAddress,
    entrypoint: "is_transfer_finalised",
    calldata: [`0x${nonce.toString(16)}`],
  });
  const first = result?.[0];
  // Pathfinder / Juno return an empty array when the entrypoint
  // reverts (e.g. the contract doesn't expose this method) — treat
  // that as "not finalised" rather than throw a SyntaxError from
  // BigInt("").
  const finalised = !!first && first !== "0x0" && BigInt(first) !== 0n;
  return { kind: "view", value: finalised };
}

// ─── helpers ────────────────────────────────────────────────────────────

function splitOmniAddress(input: string): [string | null, string | null] {
  const idx = input.indexOf(":");
  if (idx <= 0) return [null, null];
  return [input.slice(0, idx).toLowerCase(), input.slice(idx + 1)];
}

// bridge-sdk-js's ChainKind enum names the polygon variant `Pol` (not
// `Polygon`). Using `as unknown as <EvmChainKind subset>` for the cast
// since the EVM builder narrows its `chain` arg to a subset of
// ChainKind variants but doesn't export that narrowed type publicly.
const PREFIX_TO_CHAIN_KIND_EVM: Record<
  "eth" | "arb" | "base" | "bnb" | "pol" | "abs",
  ChainKind
> = {
  eth: ChainKind.Eth,
  arb: ChainKind.Arb,
  base: ChainKind.Base,
  bnb: ChainKind.Bnb,
  pol: ChainKind.Pol,
  abs: ChainKind.Abs,
};
